import { Button } from "@ternary/ui"
import { type ReactElement, useState } from "react"
import { useTranslation } from "react-i18next"
import { revokePixels } from "../analytics.js"
import { type ConsentChoice, readConsent, writeConsent } from "./tracking.js"

/**
 * The cookie banner: a non-modal bottom sheet with equal-weight Accept and Decline. It sits in the
 * page flow, sticky at the bottom, so it reserves its own height and never covers the wizard
 * footer. It shows only until the visitor chooses. Decline also withdraws consent in any pixel
 * library already on the page. `note` replaces the signup reassurance on pages that are not signup.
 */
export function ConsentBanner({
  privacyUrl,
  onChoice,
  note,
}: {
  privacyUrl: string
  note?: string
  onChoice(choice: ConsentChoice): void
}): ReactElement | null {
  const { t } = useTranslation()
  const [answered, setAnswered] = useState(() => readConsent() !== null)
  if (answered) return null

  const choose = (ads: boolean) => {
    const choice = writeConsent(ads)
    if (!ads) revokePixels()
    setAnswered(true)
    onChoice(choice)
  }

  return (
    <section aria-label={t("Cookie consent")} className="signup-consent">
      <div className="signup-consent-text">
        <p className="signup-consent-title">
          {t("We use cookies and ad pixels to measure our ads.")}
        </p>
        {/* The link sits in the note, not the button row, so the banner stays short on a phone. */}
        <p className="signup-consent-note">
          {note ?? t("You can still sign up if you decline.")}
          {privacyUrl ? (
            <>
              {" "}
              <a className="signup-consent-link" href={privacyUrl}>
                {t("Privacy Policy")}
              </a>
            </>
          ) : null}
        </p>
      </div>
      <div className="signup-consent-actions">
        <Button onClick={() => choose(false)} variant="secondary">
          {t("Decline")}
        </Button>
        <Button onClick={() => choose(true)} variant="secondary">
          {t("Accept")}
        </Button>
      </div>
    </section>
  )
}
